<template>
  <div class="row">
    <!-- Formulário -->
    <div class="col-md-5">
      <div class="card">
        <div class="card-body">
          <h6 class="mb-1">
            {{ editing ? 'Editar regra' : 'Nova regra' }}
          </h6>

          <div class="form-group">
            <label>Nome</label>
            <b-form-input
              v-model="form.name"
              placeholder="Ex: Intercorrência obrigatória"
              size="sm"
            />
          </div>

          <div class="form-group">
            <label>Quando</label>
            <v-select
              v-model="form.event"
              :options="eventOptions"
              :reduce="(o) => o.value"
              label="label"
              :clearable="false"
              class="vselect"
              @input="onEventChange"
            />
          </div>

          <div
            v-if="form.event === 'occurrence.forward'"
            class="form-group"
          >
            <label>Ao sair da etapa</label>
            <v-select
              v-model="form.from_step"
              :options="toOptions(options.steps)"
              :reduce="(o) => o.value"
              label="label"
              placeholder="Qualquer etapa"
              class="vselect"
            />
          </div>

          <div class="form-group">
            <label>Exigências</label>
            <v-select
              v-model="selectedCodes"
              :options="typeOptions"
              :reduce="(o) => o.value"
              label="label"
              multiple
              placeholder="Escolha o que a regra verifica"
              class="vselect"
            />
            <small class="text-muted">
              Todas as exigências marcadas valem juntas, com a mesma severidade e a mesma regra de dispensa.
            </small>
          </div>

          <div
            v-if="hasInvolvedScope"
            class="form-group"
          >
            <label>Aplicar aos envolvidos do papel</label>
            <v-select
              v-model="form.involved_role_id"
              :options="toOptions(options.involved_roles)"
              :reduce="(o) => o.value"
              label="label"
              placeholder="Todos os papéis"
              class="vselect"
            />
          </div>

          <!-- Parâmetros de cada exigência escolhida -->
          <div
            v-for="(requirement, index) in form.requirements"
            :key="requirement.rule_type"
            class="requirement-block"
          >
            <div class="requirement-title">
              {{ typeLabel(requirement.rule_type) }}
            </div>

            <div
              v-for="(param, key) in paramsOf(requirement.rule_type)"
              :key="key"
              class="form-group mb-50"
            >
              <label>{{ param.label }}</label>

              <b-form-input
                v-if="param.type === 'number'"
                v-model.number="form.requirements[index].config[key]"
                type="number"
                :min="param.min"
                size="sm"
              />

              <v-select
                v-else
                v-model="form.requirements[index].config[key]"
                :options="paramOptions(param)"
                :reduce="(o) => o.value"
                label="label"
                :multiple="isMulti(param)"
                class="vselect"
              />
            </div>

            <small
              v-if="!Object.keys(paramsOf(requirement.rule_type)).length"
              class="text-muted"
            >
              Sem parâmetros.
            </small>
          </div>

          <hr>

          <div
            v-if="form.event === 'finalize'"
            class="form-group"
          >
            <label>Só com estes desfechos</label>
            <v-select
              v-model="form.outcome_ids"
              :options="toOptions(options.outcomes)"
              :reduce="(o) => o.value"
              label="label"
              multiple
              placeholder="Qualquer desfecho"
              class="vselect"
            />
          </div>

          <div class="form-group">
            <label>Só para estes perfis</label>
            <v-select
              v-model="form.role_ids"
              :options="toOptions(options.roles)"
              :reduce="(o) => o.value"
              label="label"
              multiple
              placeholder="Qualquer perfil"
              class="vselect"
            />
          </div>

          <div class="form-group">
            <b-form-checkbox v-model="showScope">
              Restringir por tipo, classificação, sexo ou idade
            </b-form-checkbox>
          </div>

          <div v-if="showScope">
            <div class="form-group">
              <label>Tipo de ocorrência</label>
              <v-select
                v-model="form.applies_when.dispatch_type_ids"
                :options="toOptions(options.dispatch_types)"
                :reduce="(o) => o.value"
                label="label"
                multiple
                placeholder="Qualquer tipo"
                class="vselect"
              />
            </div>

            <div class="form-group">
              <label>Classificação de risco</label>
              <v-select
                v-model="form.applies_when.classification_ids"
                :options="toOptions(options.classifications)"
                :reduce="(o) => o.value"
                label="label"
                multiple
                placeholder="Qualquer classificação"
                class="vselect"
              />
            </div>

            <div class="row">
              <div class="col-4 form-group">
                <label>Sexo</label>
                <v-select
                  v-model="form.applies_when.involved_gender"
                  :options="genderOptions"
                  :reduce="(o) => o.value"
                  label="label"
                  placeholder="Qualquer"
                  class="vselect"
                />
              </div>
              <div class="col-4 form-group">
                <label>Idade mín.</label>
                <b-form-input
                  v-model.number="form.applies_when.involved_age_min"
                  type="number"
                  size="sm"
                />
              </div>
              <div class="col-4 form-group">
                <label>Idade máx.</label>
                <b-form-input
                  v-model.number="form.applies_when.involved_age_max"
                  type="number"
                  size="sm"
                />
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>Caso não atenda</label>
            <v-select
              v-model="form.severity"
              :options="severityOptions"
              :reduce="(o) => o.value"
              label="label"
              :clearable="false"
              class="vselect"
            />
          </div>

          <div class="form-group">
            <b-form-checkbox v-model="form.allows_override">
              Permitir dispensa com justificativa
            </b-form-checkbox>
          </div>

          <div class="form-group">
            <label>Mensagem (opcional)</label>
            <b-form-input
              v-model="form.message"
              placeholder="Deixe vazio para a mensagem automática"
              size="sm"
            />
          </div>

          <div class="form-group">
            <label>Válida para ocorrências abertas a partir de</label>
            <b-form-input
              v-model="form.effective_from"
              type="date"
              size="sm"
            />
            <small class="text-muted">
              Vazio: vale para todas, inclusive as já abertas.
            </small>
          </div>

          <div class="d-flex">
            <b-button
              size="sm"
              variant="primary"
              :disabled="saving"
              @click="save"
            >
              <b-spinner
                v-if="saving"
                small
                class="mr-50"
              />
              {{ editing ? 'Salvar' : 'Criar regra' }}
            </b-button>
            <b-button
              v-if="editing"
              size="sm"
              variant="outline-secondary"
              class="ml-1"
              @click="resetForm"
            >
              Cancelar
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Listagem -->
    <div class="col-md-7">
      <div class="card">
        <div class="card-body">
          <div class="d-flex align-items-center mb-1">
            <h6 class="mb-0">
              Regras cadastradas
            </h6>
            <b-spinner
              v-if="loading"
              small
              class="ml-1"
            />
          </div>

          <p
            v-if="!loading && !rules.length"
            class="text-muted mb-0"
          >
            Nenhuma regra nesta disciplina — o comportamento é o padrão do sistema.
          </p>

          <div
            v-for="rule in rules"
            :key="rule.id"
            class="border rounded p-1 mb-1"
          >
            <div class="d-flex align-items-start">
              <div class="flex-grow-1">
                <div class="d-flex align-items-center mb-25">
                  <b-badge
                    :variant="rule.severity === 'error' ? 'danger' : 'warning'"
                    class="mr-50"
                  >
                    {{ rule.severity === 'error' ? 'Bloqueia' : 'Avisa' }}
                  </b-badge>
                  <strong>{{ rule.name }}</strong>
                  <b-badge
                    v-if="!rule.is_active"
                    variant="secondary"
                    class="ml-50"
                  >
                    Inativa
                  </b-badge>
                </div>
                <small class="text-muted">{{ rule.sentence }}</small>
              </div>

              <div class="ml-1 text-nowrap">
                <b-button
                  size="sm"
                  variant="flat-secondary"
                  title="Ativar/desativar"
                  @click="toggleActive(rule)"
                >
                  <feather-icon :icon="rule.is_active ? 'PauseIcon' : 'PlayIcon'" />
                </b-button>
                <b-button
                  size="sm"
                  variant="flat-primary"
                  title="Editar"
                  @click="edit(rule)"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
                <b-button
                  size="sm"
                  variant="flat-danger"
                  title="Excluir"
                  @click="remove(rule)"
                >
                  <feather-icon icon="Trash2Icon" />
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BButton, BBadge, BSpinner, BFormInput, BFormCheckbox,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import swal from 'sweetalert'

export default {
  components: {
    BButton, BBadge, BSpinner, BFormInput, BFormCheckbox, vSelect,
  },

  props: {
    disciplineId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      editing: null,
      showScope: false,
      rules: [],
      types: [],
      options: {},
      events: {},
      severities: {},
      form: this.emptyForm(),
      genderOptions: [
        { value: 'M', label: 'Masculino' },
        { value: 'F', label: 'Feminino' },
      ],
    }
  },

  computed: {
    eventOptions() {
      return this.toOptions(this.events)
    },

    severityOptions() {
      return this.toOptions(this.severities)
    },

    selectedCodes: {
      get() {
        return this.form.requirements.map(r => r.rule_type)
      },
      set(codes) {
        this.form.requirements = codes.map(code => {
          const existing = this.form.requirements.find(r => r.rule_type === code)
          return existing || { rule_type: code, config: this.defaultConfig(code) }
        })
      },
    },

    hasInvolvedScope() {
      return this.form.requirements.some(r => {
        const type = this.types.find(t => t.code === r.rule_type)
        return type && type.scope === 'involved'
      })
    },

    typeOptions() {
      return this.types
        .filter(t => !this.form.event || t.events.includes(this.form.event))
        .map(t => ({ value: t.code, label: t.label }))
    },

  },

  mounted() {
    this.loadCatalog()
    this.load()
  },

  methods: {
    emptyForm() {
      return {
        name: '',
        event: 'occurrence.finalize',
        from_step: null,
        requirements: [],
        involved_role_id: null,
        outcome_ids: [],
        role_ids: [],
        applies_when: {
          dispatch_type_ids: [],
          classification_ids: [],
          involved_gender: null,
          involved_age_min: null,
          involved_age_max: null,
        },
        severity: 'error',
        allows_override: true,
        message: '',
        effective_from: null,
        is_active: true,
      }
    },

    toOptions(obj) {
      return Object.entries(obj || {}).map(([value, label]) => ({
        value: Number.isNaN(Number(value)) ? value : Number(value),
        label,
      }))
    },

    isMulti(param) {
      return param.type.includes('multiselect')
    },

    paramOptions(param) {
      const fromCatalog = {
        outcome_multiselect: this.options.outcomes,
        role_multiselect: this.options.roles,
        problem_multiselect: this.options.problems,
        form_multiselect: this.options.forms,
        step: this.options.steps,
      }

      return this.toOptions(fromCatalog[param.type] || param.options)
    },

    async loadCatalog() {
      try {
        const res = await axios.get(
          `${this.$store.state.api}business_rules/catalog`,
          { params: { discipline_id: this.disciplineId } },
        )
        const data = res.data.data || {}
        this.types = data.types || []
        this.options = data.options || {}
        this.events = data.events || {}
        this.severities = data.severities || {}
      } catch (e) {
        this.$message('Erro', 'Não foi possível carregar o catálogo de regras.', 'error')
      }
    },

    async load() {
      this.loading = true
      try {
        const res = await axios.get(
          `${this.$store.state.api}business_rules`,
          { params: { discipline_id: this.disciplineId } },
        )
        this.rules = res.data.data || []
      } catch (e) {
        this.rules = []
      } finally {
        this.loading = false
      }
    },

    onEventChange() {
      this.form.requirements = this.form.requirements.filter(r => {
        const type = this.types.find(t => t.code === r.rule_type)
        return type && type.events.includes(this.form.event)
      })
      if (this.form.event !== 'occurrence.finalize') this.form.outcome_ids = []
      if (this.form.event !== 'occurrence.forward') this.form.from_step = null
    },

    paramsOf(code) {
      const type = this.types.find(t => t.code === code)
      return type ? type.params : {}
    },

    typeLabel(code) {
      const type = this.types.find(t => t.code === code)
      return type ? type.label : code
    },

    defaultConfig(code) {
      const defaults = {}
      Object.entries(this.paramsOf(code)).forEach(([key, param]) => {
        if (param.default !== undefined) defaults[key] = param.default
      })
      return defaults
    },

    payload() {
      const scope = {}
      Object.entries(this.form.applies_when).forEach(([key, value]) => {
        const empty = value === null || value === '' || (Array.isArray(value) && !value.length)
        if (!empty) scope[key] = value
      })

      return {
        ...this.form,
        discipline_id: this.disciplineId,
        applies_when: Object.keys(scope).length ? scope : null,
        effective_from: this.form.effective_from || null,
      }
    },

    async save() {
      this.saving = true
      try {
        if (this.editing) {
          await axios.put(`${this.$store.state.api}business_rules/${this.editing}`, this.payload())
        } else {
          await axios.post(`${this.$store.state.api}business_rules`, this.payload())
        }
        this.$message('Sucesso', 'Regra salva.', 'success')
        this.resetForm()
        this.load()
      } catch (e) {
        const errors = e.response && e.response.data && e.response.data.errors
        const first = errors ? Object.values(errors)[0][0] : 'Erro ao salvar a regra.'
        this.$message('Erro', first, 'error')
      } finally {
        this.saving = false
      }
    },

    edit(rule) {
      this.editing = rule.id
      this.form = {
        ...this.emptyForm(),
        ...rule,
        outcome_ids: rule.outcome_ids || [],
        role_ids: rule.role_ids || [],
        requirements: (rule.requirements || []).map(r => ({
          rule_type: r.rule_type,
          config: r.config || {},
        })),
        applies_when: { ...this.emptyForm().applies_when, ...(rule.applies_when || {}) },
        effective_from: rule.effective_from ? String(rule.effective_from).substring(0, 10) : null,
      }
      this.showScope = !!rule.applies_when
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    resetForm() {
      this.editing = null
      this.showScope = false
      this.form = this.emptyForm()
    },

    async toggleActive(rule) {
      try {
        await axios.patch(`${this.$store.state.api}business_rules/${rule.id}`, {
          is_active: !rule.is_active,
        })
        this.load()
      } catch (e) {
        this.$message('Erro', 'Não foi possível alterar a regra.', 'error')
      }
    },

    async remove(rule) {
      const ok = await swal({
        title: 'Excluir regra?',
        text: rule.name,
        icon: 'warning',
        buttons: ['Cancelar', 'Excluir'],
        dangerMode: true,
      })

      if (!ok) return

      try {
        await axios.delete(`${this.$store.state.api}business_rules/${rule.id}`)
        this.load()
      } catch (e) {
        this.$message('Erro', 'Não foi possível excluir a regra.', 'error')
      }
    },
  },
}
</script>

<style scoped>
.requirement-block {
  border-left: 2px solid #7367f0;
  padding: 8px 0 4px 10px;
  margin-bottom: 10px;
}
.requirement-title {
  font-weight: 600;
  font-size: 0.82rem;
  margin-bottom: 6px;
}
</style>
